import { MaintenanceRecord, MaintenanceStatus } from '../../../../../types/api';
import {
  useUpdateMaintenanceStatusMutation,
  useUpdateMaintenanceDateMutation,
  useUpdateMaintenanceContentMutation,
  useUpdateMaintenanceQualityCheckMutation,
  useDeleteMaintenanceRecordMutation,
} from '../../maintenance.mutations';

// STD-016: writes go through TanStack mutations whose onSuccess invalidates the
// per-drone history/upcoming keys (replacing the old `maintenance-record-*`
// window events). The component-owned optimistic state (localMaintenanceUpdates)
// and its revert-on-error are PRESERVED here: that state lives in the View dialog
// (useMaintenanceRecordEditState) and is merged for display, so it is not cache
// state and is left as the existing component-driven optimism. The `onDataUpdated`
// callback is kept for non-cache side effects the caller wires up.
export const useMaintenanceRecordOperations = (droneSerial: string, onDataUpdated?: () => void) => {
  const updateStatusMutation = useUpdateMaintenanceStatusMutation();
  const updateDateMutation = useUpdateMaintenanceDateMutation();
  const updateContentMutation = useUpdateMaintenanceContentMutation();
  const updateQualityCheckMutation = useUpdateMaintenanceQualityCheckMutation();
  const deleteRecordMutation = useDeleteMaintenanceRecordMutation();

  const handleSaveMaintenanceEdit = async (
    recordId: string,
    editingStatus: MaintenanceStatus,
    editingStartDate: string,
    editingEndDate: string,
    editingContent: string,
    setLoading: (loading: boolean) => void,
    setLocalMaintenanceUpdates: React.Dispatch<
      React.SetStateAction<{ [id: string]: Partial<MaintenanceRecord> }>
    >,
    handleCancelEdit: () => void
  ) => {
    try {
      setLoading(true);
      let updatedRecord: MaintenanceRecord | null = null;

      // `editingStatus` is set only when the status Switch/Select is touched;
      // the dates and content are always non-empty by the time Save is
      // clickable (validated in MaintenanceRecordForm). The status branch
      // therefore still gates on truthiness — see #686 — to avoid writing
      // an empty string into the optimistic state when the user only edited
      // dates or description.
      const optimisticUpdates: Partial<MaintenanceRecord> = {
        start_date: editingStartDate,
        end_date: editingEndDate,
        work_description: editingContent,
        ...(editingStatus ? { status: editingStatus } : {}),
      };

      setLocalMaintenanceUpdates((prev) => ({
        ...prev,
        [recordId]: { ...prev[recordId], ...optimisticUpdates },
      }));

      if (editingStatus) {
        updatedRecord = await updateStatusMutation.mutateAsync({
          recordId,
          status: editingStatus,
          droneSerial,
        });
      }

      updatedRecord = await updateDateMutation.mutateAsync({
        recordId,
        startDate: editingStartDate,
        endDate: editingEndDate,
        droneSerial,
      });

      updatedRecord = await updateContentMutation.mutateAsync({
        recordId,
        workDescription: editingContent,
        droneSerial,
      });

      // Update local state with actual API response
      if (updatedRecord) {
        setLocalMaintenanceUpdates((prev) => ({
          ...prev,
          [recordId]: updatedRecord as Partial<MaintenanceRecord>,
        }));
      }

      // Refresh data
      if (onDataUpdated) {
        onDataUpdated();
      }

      handleCancelEdit();
    } catch (error) {
      console.error('Failed to update maintenance record:', error);
      // Revert optimistic updates on error
      setLocalMaintenanceUpdates((prev) => {
        const updated = { ...prev };
        delete updated[recordId];
        return updated;
      });
      // Re-throw original error to preserve backend error message
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleQualityCheckedToggle = async (
    recordId: string,
    checked: boolean,
    setLocalMaintenanceUpdates: React.Dispatch<
      React.SetStateAction<{ [id: string]: Partial<MaintenanceRecord> }>
    >
  ) => {
    try {
      // Apply optimistic updates immediately
      setLocalMaintenanceUpdates((prev) => ({
        ...prev,
        [recordId]: { ...prev[recordId], quality_checked: checked },
      }));

      // Call the API to update quality_checked status
      const updatedRecord = await updateQualityCheckMutation.mutateAsync({
        recordId,
        qualityChecked: checked,
        droneSerial,
      });

      // Update local state with actual API response
      setLocalMaintenanceUpdates((prev) => ({
        ...prev,
        [recordId]: updatedRecord,
      }));
    } catch (error) {
      console.error('Failed to update quality check status:', error);
      // Revert optimistic updates on error
      setLocalMaintenanceUpdates((prev) => {
        const updated = { ...prev };
        if (updated[recordId]) {
          updated[recordId] = { ...updated[recordId], quality_checked: !checked };
        }
        return updated;
      });
      // Re-throw original error to preserve backend error message
      throw error;
    }
  };

  const handleStatusToggle = async (
    recordId: string,
    newStatus: MaintenanceStatus,
    setLocalMaintenanceUpdates: React.Dispatch<
      React.SetStateAction<{ [id: string]: Partial<MaintenanceRecord> }>
    >
  ) => {
    try {
      // Apply optimistic updates immediately
      setLocalMaintenanceUpdates((prev) => ({
        ...prev,
        [recordId]: { ...prev[recordId], status: newStatus },
      }));

      // Call the API to update status
      const updatedRecord = await updateStatusMutation.mutateAsync({
        recordId,
        status: newStatus,
        droneSerial,
      });

      // Update local state with actual API response
      setLocalMaintenanceUpdates((prev) => ({
        ...prev,
        [recordId]: updatedRecord,
      }));
    } catch (error) {
      console.error('Failed to update status:', error);
      // Revert optimistic updates on error
      setLocalMaintenanceUpdates((prev) => {
        const updated = { ...prev };
        if (updated[recordId]) {
          // Revert to previous status - we don't know what it was, so remove the update
          delete updated[recordId].status;
        }
        return updated;
      });
      // Re-throw original error to preserve backend error message
      throw error;
    }
  };

  const handleDeleteMaintenance = async (
    recordId: string,
    onClose: () => void,
    setLoading: (loading: boolean) => void
  ) => {
    try {
      setLoading(true);

      await deleteRecordMutation.mutateAsync({ recordId, droneSerial });

      // Close dialog since the item was deleted
      onClose();
    } catch (error) {
      console.error('Failed to delete maintenance record:', error);
      // Re-throw original error to preserve backend error message
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    handleSaveMaintenanceEdit,
    handleQualityCheckedToggle,
    handleStatusToggle,
    handleDeleteMaintenance,
  };
};
